import React, { useEffect, useState } from 'react';
import { changelogList, changelogCreate, changelogUpdate, changelogDelete, tourLeadsList } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Sparkles, Plus, Pencil, Trash2, Loader2, Save, X, FileText, Bug, ShieldCheck, Zap, Palette, Wrench, Rocket, Filter, DollarSign, Clock, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';

const CAT_META = {
  feature:     { label: 'Feature',     icon: Sparkles, color: '#22d3ee' },
  improvement: { label: 'Improvement', icon: Zap,      color: '#a78bfa' },
  bugfix:      { label: 'Bug fix',     icon: Bug,      color: '#ff6b8a' },
  security:    { label: 'Security',    icon: ShieldCheck, color: '#ffcf52' },
  infra:       { label: 'Infra',       icon: Wrench,   color: '#94a3b8' },
  rebrand:     { label: 'Rebrand',     icon: Palette,  color: '#ff7a00' },
};
const STATUS_META = {
  shipped:  { label: 'Shipped',  icon: CheckCircle2, color: '#22d3ee' },
  beta:     { label: 'In beta',  icon: Rocket,       color: '#a78bfa' },
  planned:  { label: 'Planned',  icon: Clock,        color: '#ffcf52' },
  archived: { label: 'Archived', icon: AlertTriangle,color: '#94a3b8' },
};

// ==================== PUBLIC CHANGELOG ====================
export default function Changelog() {
  const { user } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat !== 'all') params.category = cat;
      if (status !== 'all') params.status = status;
      setItems(await changelogList(params));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, status]);

  const isStaff = user && (user.role === 'admin' || user.role === 'owner');

  return (
    <div data-testid="changelog-page" className="max-w-[1100px] mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[#22d3ee]"><Sparkles className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">What's new</span></div>
        <h1 className="font-display text-[36px] md:text-[52px] mt-2 leading-[1.02]">The <span className="brand-gradient">ReaperCast</span> changelog.</h1>
        <p className="text-white/65 text-[15px] mt-3 max-w-[760px]">Every feature we've shipped, what's in beta, and what's planned — with honest pricing notes.</p>
        {isStaff && <button onClick={() => nav('/admin/changelog')} className="btn-primary mt-4" data-testid="changelog-manage-btn"><Pencil className="w-4 h-4" /> Manage entries</button>}
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-3.5 h-3.5 text-white/45" />
        <button onClick={() => setCat('all')} className={`chip ${cat === 'all' ? 'active' : ''}`}>All kinds</button>
        {Object.entries(CAT_META).map(([k, m]) => (
          <button key={k} onClick={() => setCat(k)} className={`chip ${cat === k ? 'active' : ''}`} data-testid={`changelog-cat-${k}`}><m.icon className="w-3 h-3 inline mr-1" /> {m.label}</button>
        ))}
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button onClick={() => setStatus('all')} className={`chip ${status === 'all' ? 'active' : ''}`}>All status</button>
        {Object.entries(STATUS_META).filter(([k]) => k !== 'archived').map(([k, m]) => (
          <button key={k} onClick={() => setStatus(k)} className={`chip ${status === k ? 'active' : ''}`} data-testid={`changelog-status-${k}`}><m.icon className="w-3 h-3 inline mr-1" /> {m.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-shell p-10 text-center text-white/55">No entries match these filters.</div>
      ) : (
        <div className="space-y-4">
          {items.map(c => {
            const CM = CAT_META[c.category] || CAT_META.feature;
            const SM = STATUS_META[c.status] || STATUS_META.shipped;
            return (
              <article key={c.id} className="card-shell p-5 flex gap-5 hover-lift" data-testid={`changelog-${c.id}`}>
                <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: (c.color || CM.color) + '22', color: c.color || CM.color }}>
                  <CM.icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="chip uppercase text-[10px]" style={{ color: CM.color, borderColor: CM.color + '55' }}><CM.icon className="w-3 h-3 mr-1 inline" />{CM.label}</span>
                    <span className={`chip uppercase text-[10px] ${c.status === 'shipped' ? 'active' : ''}`} style={{ color: SM.color, borderColor: SM.color + '55' }}><SM.icon className="w-3 h-3 mr-1 inline" />{SM.label}</span>
                    {c.version && <span className="text-[11px] text-white/45 tabular-nums">v{c.version}</span>}
                    <span className="text-[11px] text-white/45 ml-auto">{c.released_at ? new Date(c.released_at).toLocaleDateString() : ''}</span>
                  </div>
                  <h3 className="font-display text-[20px] mt-2">{c.title}</h3>
                  <p className="text-white/70 text-[13.5px] mt-1.5 leading-relaxed">{c.summary}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-[12px]">
                    {c.user_price_note && <span className="inline-flex items-center gap-1 text-[#22d3ee]"><DollarSign className="w-3 h-3" /> You: {c.user_price_note}</span>}
                    {c.tier_required && c.tier_required !== 'free' && <span className="chip text-[10px] uppercase">{c.tier_required}</span>}
                    {isStaff && c.internal_cost_note && <span className="inline-flex items-center gap-1 text-[#ffcf52]"><Wrench className="w-3 h-3" /> Us: {c.internal_cost_note}</span>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== ADMIN CHANGELOG ====================
export function AdminChangelog() {
  const { user } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [composing, setComposing] = useState(false);

  const staff = user && (user.role === 'admin' || user.role === 'owner');

  const load = async () => {
    setLoading(true);
    try {
      const [cl, ld] = await Promise.all([changelogList(), tourLeadsList().catch(() => [])]);
      setItems(cl); setLeads(ld);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (staff) load(); }, [staff]);

  if (!staff) return <div className="text-center text-white/60 py-16">Admin/Owner access required.</div>;

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await changelogDelete(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-changelog" className="max-w-[1180px] mx-auto">
      <Link to="/changelog" className="btn-ghost text-[13px] mb-3 inline-flex"><ArrowLeft className="w-4 h-4" /> View public changelog</Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#ffcf52]"><Wrench className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Admin · App Settings</span></div>
          <h1 className="font-display text-[32px] mt-1">Changelog &amp; Tour leads</h1>
        </div>
        <button onClick={() => setComposing(true)} className="btn-primary" data-testid="changelog-new-btn"><Plus className="w-4 h-4" /> New entry</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
        <section>
          <div className="text-[12px] uppercase tracking-wider text-white/55 mb-2">Entries ({items.length})</div>
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-white/60" /> : (
            <div className="space-y-2">
              {items.map(c => (
                <div key={c.id} className="card-shell p-3 flex items-center gap-3" data-testid={`admin-cl-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="chip uppercase text-[10px]">{c.category}</span>
                      <span className={`chip uppercase text-[10px] ${c.status === 'shipped' ? 'active' : ''}`}>{c.status}</span>
                      {c.tier_required && c.tier_required !== 'free' && <span className="chip text-[10px]">{c.tier_required}</span>}
                    </div>
                    <div className="font-semibold mt-1 truncate">{c.title}</div>
                    <div className="text-[11.5px] text-white/50 mt-0.5 grid gap-0.5">
                      <div>User: {c.user_price_note || '—'}</div>
                      <div>Us: {c.internal_cost_note || '—'}</div>
                    </div>
                  </div>
                  <button onClick={() => setEditing(c)} className="btn-ghost text-[12px]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(c.id)} className="text-white/50 hover:text-[#ff6b8a]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside>
          <div className="text-[12px] uppercase tracking-wider text-white/55 mb-2">Tour leads ({leads.length})</div>
          <div className="card-shell p-3 max-h-[680px] overflow-y-auto">
            {leads.length === 0 && <div className="text-white/55 text-[13px] py-6 text-center">No tour leads yet.</div>}
            <div className="space-y-1.5">
              {leads.map(l => (
                <div key={l.id} className="border border-white/10 rounded-lg p-2.5" data-testid={`admin-lead-${l.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip text-[10px] uppercase">{l.path}</span>
                    {l.completed && <span className="chip active text-[10px]">✓ done</span>}
                    <span className="text-[11px] text-white/50 ml-auto">step {l.step_reached}</span>
                  </div>
                  <div className="text-[13px] mt-1">{l.name || '(no name)'} {l.email && <span className="text-white/55">· {l.email}</span>}</div>
                  {l.interests?.length > 0 && <div className="text-[11.5px] text-white/50 mt-0.5">Interests: {l.interests.join(', ')}</div>}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {(editing || composing) && <ChangelogEditor item={editing} onClose={() => { setEditing(null); setComposing(false); }} onSaved={() => { setEditing(null); setComposing(false); load(); }} />}
    </div>
  );
}

function ChangelogEditor({ item, onClose, onSaved }) {
  const [f, setF] = useState({
    title: item?.title || '', summary: item?.summary || '', version: item?.version || '',
    category: item?.category || 'feature', status: item?.status || 'shipped',
    tier_required: item?.tier_required || 'free',
    user_price_note: item?.user_price_note || '', internal_cost_note: item?.internal_cost_note || '',
    color: item?.color || '', icon: item?.icon || '',
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.title.trim() || !f.summary.trim()) return toast.error('Title and summary required');
    setBusy(true);
    try {
      if (item) await changelogUpdate(item.id, f);
      else await changelogCreate(f);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0c] border border-white/15 rounded-2xl w-full max-w-[680px] max-h-[92vh] overflow-y-auto p-6" data-testid="changelog-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[22px]">{item ? 'Edit entry' : 'New changelog entry'}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <input value={f.title} onChange={e => upd('title', e.target.value)} placeholder="Title (e.g. 'Watch Parties')" className="input-field" maxLength={200} data-testid="cl-editor-title" />
          <textarea value={f.summary} onChange={e => upd('summary', e.target.value)} rows={4} placeholder="Summary — what changed, why it matters" className="input-field resize-none" maxLength={2000} data-testid="cl-editor-summary" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={e => upd('category', e.target.value)} className="input-field" data-testid="cl-editor-category">
              {Object.entries(CAT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <select value={f.status} onChange={e => upd('status', e.target.value)} className="input-field">
              {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <select value={f.tier_required} onChange={e => upd('tier_required', e.target.value)} className="input-field">
              {['free','starter','pro','label','plus'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={f.version} onChange={e => upd('version', e.target.value)} placeholder="Version (e.g. 3.0.0)" className="input-field" />
          </div>
          <input value={f.user_price_note} onChange={e => upd('user_price_note', e.target.value)} placeholder="What USER pays (e.g. 'Free', 'Plus $9/mo')" className="input-field" data-testid="cl-editor-userprice" />
          <input value={f.internal_cost_note} onChange={e => upd('internal_cost_note', e.target.value)} placeholder="What WE pay (admin-only; e.g. '$0.02/query Claude')" className="input-field" data-testid="cl-editor-ourcost" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="cl-editor-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
        </div>
      </div>
    </div>
  );
}
